import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { cancelOrder, fetchOrders } from '../api/orders'
import type { ApiOrder, OrderStatus } from '../api/types'
import { ORDER_LIST_LIMIT, ORDER_UI } from '../constants/orders'
import { QUERY_KEYS } from '../constants/query'
import { toApiMessage } from '../helpers/api-error'
import { replaceOrder } from '../helpers/orders'
import { useAuth } from './useAuth'

export type UseOrdersOptions = {
  status?: OrderStatus | string
  limit?: number
}

type UseOrdersResult = {
  orders: ApiOrder[]
  loading: boolean
  error: string | null
  cancellingId: string | null
  cancel: (id: string, reason?: string) => Promise<boolean>
  refresh: () => Promise<void>
}

export function useOrders(options?: UseOrdersOptions): UseOrdersResult {
  const { isAuthenticated } = useAuth()
  const queryClient = useQueryClient()
  const [cancellingId, setCancellingId] = useState<string | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)

  const statusParam =
    options?.status && options.status !== 'all'
      ? (options.status as OrderStatus)
      : undefined
  const limitParam = options?.limit ?? ORDER_LIST_LIMIT

  const ordersQuery = useQuery({
    queryKey: QUERY_KEYS.ordersByStatus(statusParam),
    queryFn: () =>
      fetchOrders({
        status: statusParam,
        limit: limitParam,
      }),
    enabled: isAuthenticated,
  })

  const error = !isAuthenticated
    ? null
    : actionError ??
      (ordersQuery.error
        ? toApiMessage(ordersQuery.error, ORDER_UI.loadError)
        : null)

  async function cancel(id: string, reason?: string) {
    setCancellingId(id)
    setActionError(null)
    try {
      const next = await cancelOrder(id, reason)
      queryClient.setQueryData<ApiOrder[]>(
        QUERY_KEYS.ordersByStatus(statusParam),
        (current) => replaceOrder(current, next),
      )
      queryClient.setQueryData<ApiOrder[]>(QUERY_KEYS.ordersByStatus(undefined), (current) =>
        replaceOrder(current, next),
      )
      queryClient.setQueryData(QUERY_KEYS.order(id), next)
      return true
    } catch (err) {
      setActionError(toApiMessage(err, ORDER_UI.loadError))
      return false
    } finally {
      setCancellingId(null)
    }
  }

  return {
    orders: isAuthenticated ? (ordersQuery.data ?? []) : [],
    loading:
      isAuthenticated &&
      ordersQuery.isPending &&
      !ordersQuery.data &&
      !ordersQuery.error,
    error,
    cancellingId,
    cancel,
    refresh: async () => {
      if (!isAuthenticated) return
      await ordersQuery.refetch()
    },
  }
}
